import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from '@material-ui/core/CircularProgress';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { makeStyles } from '@material-ui/core/styles';

import { green } from '@material-ui/core/colors';

import api from '../../services/api';
import toastError from '../../errors/toastError';
import { i18n } from '../../translate/i18n';
import usePlans from '../../hooks/usePlans';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const ProductSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Nome muito curto!')
    .max(50, 'Nome muito longo!')
    .required('Nome é obrigatório'),
  type: Yup.string().required('Tipo é obrigatório'),
  value: Yup.number()
    .min(0, 'Preço deve ser maior que zero')
    .required('Preço é obrigatório'),
  description: Yup.string().max(500, 'Descrição muito longa!'),
  schedule: Yup.boolean(),
  isAvailable: Yup.boolean().required(),
  email: Yup.string().when('schedule', {
    is: true,
    then: Yup.string()
      .email('Email inválido')
      .required('Email é obrigatório para agendamento'),
    otherwise: Yup.string().email('Email inválido').nullable(),
  }),
});

const ProductModal = ({ open, onClose, productId }) => {
  const classes = useStyles();
  const history = useHistory();
  const { getPlanCompany } = usePlans();

  const initialState = {
    name: '',
    type: 'product',
    value: '',
    description: '',
    email: '',
    isAvailable: true,
    schedule: false,
  };

  const [product, setProduct] = useState(initialState);
  const [originalProduct, setOriginalProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isGoogleCalendarEnabled, setIsGoogleCalendarEnabled] = useState(false);
  const [integrationTestSuccessful, setIntegrationTestSuccessful] =
    useState(false);
  const [testingIntegration, setTestingIntegration] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;
      try {
        const { data } = await api.get(`/products/${productId}`);
        const productData = {
          ...data,
          schedule: Boolean(data.email && data.email.trim() !== ''),
        };
        setProduct(productData);
        setOriginalProduct(productData);
        if (productData.schedule && productData.email) {
          setIntegrationTestSuccessful(true);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchProduct();
  }, [productId, open]);

  // Verificar se Google Calendar está ativo no plano da empresa
  useEffect(() => {
    const checkGoogleCalendarStatus = async () => {
      if (!open) return; // Só verifica quando o modal está aberto

      try {
        const companyId = localStorage.getItem('companyId');
        if (companyId) {
          const planData = await getPlanCompany({}, companyId);
          if (planData && planData.plan) {
            setIsGoogleCalendarEnabled(
              planData.plan.useGoogleCalendar === true
            );
          }
        }
      } catch (err) {
        console.error('Erro ao verificar status do Google Calendar:', err);
        setIsGoogleCalendarEnabled(false);
      }
    };

    checkGoogleCalendarStatus();
  }, [open, getPlanCompany]);

  // Sincronizar campo schedule com email
  useEffect(() => {
    if (product.email !== undefined) {
      const hasEmail = Boolean(product.email && product.email.trim() !== '');
      if (product.schedule !== hasEmail) {
        setProduct(prev => ({ ...prev, schedule: hasEmail }));
      }
    }
  }, [product.email, product.schedule]);

  const handleClose = () => {
    onClose();
    setProduct(initialState);
    setOriginalProduct(null);
    setIntegrationTestSuccessful(false);
  };

  const handleTestIntegration = async email => {
    if (!email || !email.trim()) {
      toast.warning('Informe o e-mail antes de testar a integração.');
      return;
    }
    setTestingIntegration(true);
    try {
      const testStart = moment('2050-01-15 10:00').toISOString();
      const testEnd = moment('2050-01-15 10:30').toISOString();
      await api.post('/calendar/google-event', {
        eventData: {
          title: '[TESTE] Integração Google Calendar - pode excluir',
          description: 'Evento de teste criado ao configurar produto/serviço.',
          location: 'Teste',
          startDateTime: testStart,
          endDateTime: testEnd,
          timeZone: 'America/Sao_Paulo',
        },
        doctorEmail: email.trim(),
      });
      setIntegrationTestSuccessful(true);
      toast.success('Integração com Google Calendar verificada com sucesso!');
    } catch (err) {
      setIntegrationTestSuccessful(false);
      toastError(err);
    } finally {
      setTestingIntegration(false);
    }
  };

  const handleSaveProduct = async values => {
    setLoading(true);
    const companyId = localStorage.getItem('companyId');
    try {
      if (productId) {
        // Enviar apenas os campos que foram alterados
        const updateData = {};

        if (!originalProduct) {
          toast.error('Erro: Produto original não encontrado');
          setLoading(false);
          return;
        }

        // Comparar cada campo com o valor original
        if (values.name !== originalProduct.name) {
          updateData.name = values.name;
        }
        if (values.type !== originalProduct.type) {
          updateData.type = values.type;
        }
        if (Number(values.value) !== Number(originalProduct.value)) {
          updateData.value = values.value;
        }
        if (values.description !== originalProduct.description) {
          updateData.description = values.description;
        }
        if (values.email !== originalProduct.email) {
          updateData.email = values.email;
        }
        if (
          Boolean(values.isAvailable) !== Boolean(originalProduct.isAvailable)
        ) {
          updateData.isAvailable = values.isAvailable;
        }
        // Comparar campo schedule baseado na presença do email
        const currentSchedule = Boolean(
          values.email && values.email.trim() !== ''
        );
        const originalSchedule = Boolean(
          originalProduct.email && originalProduct.email.trim() !== ''
        );
        if (currentSchedule !== originalSchedule) {
          updateData.schedule = currentSchedule;
        }

        // Só fazer a requisição se houver mudanças
        if (Object.keys(updateData).length > 0) {
          await api.put(`/products/${productId}`, { ...updateData, companyId });
          toast.success(i18n.t('products.toasts.updated'));
        } else {
          toast.info('Nenhuma alteração foi feita');
        }
      } else {
        await api.post('/products', { ...values, companyId });
        toast.success(i18n.t('products.toasts.created'));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title'>
          {productId
            ? `${i18n.t('productModal.title.edit')}`
            : `${i18n.t('productModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={product}
          enableReinitialize={true}
          validationSchema={ProductSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveProduct(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.root}>
                  <Field
                    as={TextField}
                    label={i18n.t('productModal.form.name')}
                    name='name'
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant='outlined'
                    margin='dense'
                    className={classes.textField}
                    fullWidth
                  />
                </div>

                <div className={classes.root}>
                  <FormControl
                    variant='outlined'
                    className={classes.textField}
                    margin='dense'
                    error={touched.type && Boolean(errors.type)}
                  >
                    <InputLabel>{i18n.t('productModal.form.type')}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t('productModal.form.type')}
                      name='type'
                      onChange={e => {
                        setFieldValue('type', e.target.value);
                        if (e.target.value !== 'service') {
                          setFieldValue('email', '');
                          setFieldValue('schedule', false);
                        }
                      }}
                    >
                      <MenuItem value='product'>Produto</MenuItem>
                      <MenuItem value='service'>Serviço</MenuItem>
                    </Field>
                  </FormControl>
                  {/* <Field
                    as={TextField}
                    label={i18n.t("productModal.form.amount")}
                    name="amount"
                    type="number"
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    // InputProps={{
                    //   startAdornment: (
                    //     <InputAdornment position="start">R$</InputAdornment>
                    //   ),
                    //   inputProps: { min: 0, step: 0.01 },
                    // }}
                  /> */}

                  <Field
                    as={TextField}
                    label={i18n.t('productModal.form.price')}
                    name='value'
                    type='number'
                    error={touched.value && Boolean(errors.value)}
                    helperText={touched.value && errors.value}
                    variant='outlined'
                    margin='dense'
                    className={classes.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>R$</InputAdornment>
                      ),
                      inputProps: { min: 0, step: 0.01 },
                    }}
                  />
                </div>

                <div className={classes.root}>
                  <Field
                    as={TextField}
                    label={i18n.t('productModal.form.description')}
                    name='description'
                    multiline
                    rows={3}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    variant='outlined'
                    margin='dense'
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                {values.type === 'service' && isGoogleCalendarEnabled && (
                  <div className={classes.root}>
                    <Box display='flex' alignItems='center' gap={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.schedule}
                            onChange={e => {
                              const newValue = e.target.checked;
                              setFieldValue('schedule', newValue);
                              if (!newValue) {
                                setFieldValue('email', '');
                                setIntegrationTestSuccessful(false);
                              }
                            }}
                            name='schedule'
                            color='primary'
                          />
                        }
                        label={i18n.t('productModal.form.schedule')}
                      />
                      <Tooltip title='Como realizar a integração'>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() => history.push('/helps')}
                          style={{ padding: 4 }}
                        >
                          <HelpOutlineIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </div>
                )}

                {values.type === 'service' &&
                  values.schedule &&
                  isGoogleCalendarEnabled && (
                  <>
                    <div className={classes.root}>
                      <Field
                        as={TextField}
                        label={i18n.t('productModal.form.email')}
                        name='email'
                        type='email'
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        variant='outlined'
                        margin='dense'
                        className={classes.textField}
                        fullWidth
                        onChange={e => {
                          setFieldValue('email', e.target.value);
                          setIntegrationTestSuccessful(false);
                        }}
                      />
                    </div>
                    <div
                      className={classes.root}
                      style={{
                        marginTop: 8,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <Button
                        variant='outlined'
                        color='secondary'
                        disabled={
                          !values.email?.trim() ||
                          testingIntegration ||
                          Boolean(errors.email)
                        }
                        onClick={() => handleTestIntegration(values.email)}
                        className={classes.btnWrapper}
                      >
                        {testingIntegration ? (
                          <>
                            <CircularProgress size={20} style={{ marginRight: 8 }} />
                            Testando...
                          </>
                        ) : integrationTestSuccessful ? (
                          '✓ Integração verificada'
                        ) : (
                          'Testar Agendamento'
                        )}
                      </Button>
                      {integrationTestSuccessful && (
                        <Typography
                          variant='caption'
                          color='primary'
                          style={{ marginLeft: 12, alignSelf: 'center' }}
                        >
                          Integração com Google Calendar verificada.
                        </Typography>
                      )}
                    </div>
                  </>
                )}

                <div className={classes.root}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isAvailable}
                        onChange={e =>
                          setFieldValue('isAvailable', e.target.checked)
                        }
                        name='isAvailable'
                        color='primary'
                      />
                    }
                    label={i18n.t('productModal.form.active')}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color='secondary'
                  disabled={isSubmitting}
                  variant='outlined'
                >
                  {i18n.t('productModal.buttons.cancel')}
                </Button>
                <div className={classes.btnWrapper}>
                  <Button
                    type='submit'
                    color='primary'
                    disabled={
                      isSubmitting ||
                      (values.schedule && !integrationTestSuccessful)
                    }
                    variant='contained'
                    className={classes.textField}
                    title={
                      values.schedule && !integrationTestSuccessful
                        ? 'Teste a integração com Google Calendar antes de salvar'
                        : ''
                    }
                  >
                    {productId
                      ? `${i18n.t('productModal.buttons.okEdit')}`
                      : `${i18n.t('productModal.buttons.okAdd')}`}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ProductModal;
